import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User as UserIcon, Phone, Mail, Calendar, BookOpen, Loader2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/user`;

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/profile`, { withCredentials: true });
        if (cancelled) return;
        setProfile(res.data);
        setFullName(res.data.full_name || '');
        setEmail(res.data.email || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    const body = {};
    const nextName = fullName.trim();
    const nextEmail = email.trim().toLowerCase();
    if (nextName !== (profile?.full_name || '')) body.full_name = nextName;
    if (nextEmail !== (profile?.email || '').toLowerCase()) body.email = nextEmail;
    if (Object.keys(body).length === 0) {
      toast.message('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, body, { withCredentials: true });
      toast.success('Profile updated');
      setProfile((p) => ({ ...p, ...body }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B375B]" />
      </div>
    );
  }

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div className="max-w-3xl mx-auto" data-testid="b2c-profile-page">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">My Profile</h1>
      <p className="text-sm text-gray-600 mb-6">Manage your personal details. Phone number is your login identity and cannot be changed.</p>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
        {/* Avatar + identity strip */}
        <div className="flex items-center gap-4 pb-6 border-b border-gray-100 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1B375B] to-[#335E77] flex items-center justify-center text-white text-2xl font-bold shadow-sm">
            {(profile?.full_name || profile?.phone || '?').trim().charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-semibold text-gray-900 text-lg">{profile?.full_name || 'Add your name below'}</div>
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
              <Phone className="w-3.5 h-3.5" />
              <span>{profile?.phone || '—'}</span>
            </div>
          </div>
        </div>

        {/* Editable fields */}
        <div className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
              <UserIcon className="w-4 h-4" /> Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name (as on government ID)"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B375B] focus:border-transparent"
              data-testid="profile-full-name-input"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
              <Mail className="w-4 h-4" /> Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B375B] focus:border-transparent"
              data-testid="profile-email-input"
            />
            <p className="text-xs text-gray-500 mt-1">Used for booking confirmations and e-tickets.</p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
              <Phone className="w-4 h-4" /> Phone (login identity)
            </label>
            <input
              type="text"
              value={profile?.phone || ''}
              disabled
              className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg cursor-not-allowed"
            />
          </div>
        </div>

        {/* Read-only stats */}
        <div className="mt-7 pt-5 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-2.5">
            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <div className="text-gray-500">Member since</div>
              <div className="font-medium text-gray-900">{memberSince}</div>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <BookOpen className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <div className="text-gray-500">Total bookings</div>
              <div className="font-medium text-gray-900" data-testid="profile-total-bookings">{profile?.total_bookings ?? 0}</div>
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="mt-7 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1B375B] text-white rounded-lg font-semibold hover:bg-[#335E77] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
